/**
 * PhaseBackground Component
 *
 * Renders production phases as solid swimlane segments with clean styling.
 * Matches the visual style of ProcessListPage swimlanes.
 *
 * Visual Logic:
 * - Shadow batches: lighter shade (reduced opacity)
 * - Completed phases: vibrant, full saturation
 * - Active/planned phases: slightly muted
 * - White text with shadow for legibility
 * - Subtle borders between segments
 */

import React from 'react'
import { cn } from '@/lib/utils'

/**
 * PhaseBackground Component
 *
 * @param {Object} props
 * @param {Object} props.phase - Phase data (name, label, startDate, endDate, status, color, duration)
 * @param {Object} props.variance - Variance configuration for this phase (unused, kept for API compatibility)
 * @param {Object} props.timelineScale - Timeline positioning data
 * @param {Date} props.timelineScale.rangeStart - Timeline start date
 * @param {number} props.timelineScale.pixelsPerDay - Pixels per day scale
 * @param {number} props.height - Height of the background (default: full row height)
 * @param {boolean} props.isShadow - Whether this is a shadow (planned) batch
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.phaseIndex - Index of this phase (for shade variation)
 */
export default function PhaseBackground({
  phase,
  variance,
  timelineScale,
  height = '100%',
  isShadow = false,
  className,
  phaseIndex = 0
}) {
  const { rangeStart, pixelsPerDay } = timelineScale

  // Calculate horizontal positioning
  const daysBetween = (date1, date2) => {
    const oneDay = 24 * 60 * 60 * 1000
    return Math.round((date2 - date1) / oneDay)
  }

  const startOffset = daysBetween(rangeStart, phase.startDate) * pixelsPerDay
  const width = phase.duration * pixelsPerDay

  // Determine opacity based on status:
  // - Shadow batches: lighter (0.35 opacity)
  // - Completed phases: vibrant (full opacity)
  // - Active/planned: slightly muted (0.85 opacity)
  const isCompleted = phase.status === 'completed'
  const opacity = isShadow
    ? 0.35
    : isCompleted
      ? 1
      : 0.85

  // Minimum width for showing label (40px for shorter labels)
  const showLabel = width >= 40

  return (
    <div
      className={cn(
        'absolute top-0 flex items-center justify-center overflow-hidden',
        'transition-all hover:brightness-110',
        className
      )}
      style={{
        left: startOffset,
        width: Math.max(width, 4), // Minimum 4px width for visibility
        height,
        backgroundColor: phase.color,
        opacity,
        borderRight: '1px solid rgba(255,255,255,0.2)'
      }}
    >
      {/* Phase label with white text and shadow for legibility */}
      {showLabel && (
        <span
          className="text-[10px] font-medium truncate px-1 whitespace-nowrap"
          style={{
            color: '#fff',
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }}
        >
          {phase.label}
        </span>
      )}
    </div>
  )
}

/**
 * Phase color constants for reference
 * (Should match ProductionTimeline.jsx)
 */
export const PHASE_COLORS = {
  spawn_incubation: '#8b5cf6',  // purple
  spawn_run: '#f59e0b',         // amber
  pinning: '#10b981',           // emerald
  interval: '#3b82f6',          // blue
  spent_substrate: '#6b7280'    // gray
}
